import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { fmtError } from "@/lib/api";
import { fmtINR } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Trophy, Flame, Medal, Target, Settings as SettingsIcon, Users, Sparkles,
  CheckCircle2, XCircle, AlertTriangle, TrendingUp, Loader2, ShieldCheck,
  Crown, Shield, Ban, Download,
} from "lucide-react";

const TABS = [
  { key: "leaderboard", label: "Leaderboard", icon: Trophy },
  { key: "team", label: "Team Overview", icon: Users },
  { key: "milestones", label: "Milestones", icon: Medal },
  { key: "streaks", label: "Streaks", icon: Flame },
  { key: "flags", label: "Flags", icon: Shield },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

// ── Small primitives ──────────────────────────────────────────────────────

function StatBox({ label, value, tone = "default", "data-testid": testid }) {
  const toneMap = {
    default: "bg-muted/30 text-ink",
    ok: "bg-primary-50 text-primary-800 border-primary-200",
    warn: "bg-amber-50 text-amber-800 border-amber-200",
    bad: "bg-red-50 text-red-800 border-red-200",
  };
  return (
    <div className={`p-4 rounded-2xl border ${toneMap[tone] || toneMap.default}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-70">{label}</p>
      <p className="font-display text-2xl font-black mt-1 leading-none">{value}</p>
    </div>
  );
}

function ProgressBar({ value, target, tone = "primary" }) {
  const pct = target > 0 ? Math.min(100, (value / target) * 100) : 0;
  const barTone = pct >= 100 ? "bg-primary" : pct >= 60 ? "bg-amber-500" : "bg-red-400";
  return (
    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
      <div className={`h-full ${barTone} transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function ReleaseBadge({ mode }) {
  const styles = {
    draft: "bg-stone-100 text-stone-700 border-stone-200",
    internal: "bg-amber-50 text-amber-800 border-amber-200",
    live: "bg-primary-50 text-primary-700 border-primary-200",
  };
  return (
    <Badge variant="outline" className={`text-[10px] uppercase tracking-[0.18em] ${styles[mode] || styles.draft}`} data-testid="release-mode-badge">
      {mode === "draft" ? "Draft · admin only" : mode === "internal" ? "Internal test" : "Live"}
    </Badge>
  );
}

// ── Hall of Fame (past monthly winners) ──────────────────────────────────

function HallOfFame({ isAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/performance/monthly-winners", { params: { limit: 24 } });
      setRows(data.rows || []);
    } catch { /* silent — feature can be empty */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runSnapshot = async () => {
    if (!window.confirm("Run the monthly-winner snapshot for the previous calendar month now? Winners will be crowned if any reps are qualified.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/performance/admin/trigger-monthly-winners");
      if ((data.winners || []).length === 0) {
        toast.info(`No qualified reps for ${data.month} — snapshot skipped.`);
      } else {
        toast.success(`${data.winners.length} winner(s) crowned for ${data.month}`);
      }
      load();
    } catch (err) { toast.error(fmtError(err)); }
    finally { setRunning(false); }
  };

  // Group by month so the UI renders one "podium card" per month.
  const byMonth = rows.reduce((acc, r) => {
    (acc[r.month] = acc[r.month] || []).push(r);
    return acc;
  }, {});
  const months = Object.keys(byMonth).sort().reverse();

  const rankTone = {
    1: "bg-amber-50 border-amber-300 text-amber-900",
    2: "bg-stone-100 border-stone-300 text-stone-800",
    3: "bg-orange-50 border-orange-200 text-orange-900",
  };

  return (
    <Card className="rounded-3xl border-border/60" data-testid="hall-of-fame">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-500" />
            <h3 className="font-display text-lg font-extrabold tracking-tight">Hall of fame</h3>
          </div>
          {isAdmin && (
            <Button
              variant="outline" size="sm"
              onClick={runSnapshot} disabled={running}
              className="rounded-2xl tap text-xs"
              data-testid="run-monthly-winner-snapshot"
            >
              {running ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Crown className="h-3.5 w-3.5 mr-1" />}
              Snapshot last month
            </Button>
          )}
        </div>

        {loading ? (
          <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" /></div>
        ) : months.length === 0 ? (
          <div className="py-8 text-center space-y-1">
            <p className="text-sm text-muted-foreground">No monthly winners crowned yet.</p>
            <p className="text-[11px] text-muted-foreground">Runs automatically on the 1st of every month{isAdmin ? ". Use Snapshot last month to backfill." : "."}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {months.map((m) => {
              const winners = byMonth[m].sort((a, b) => a.rank - b.rank);
              return (
                <div key={m} className="space-y-2" data-testid={`hof-month-${m}`}>
                  <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{winners[0]?.month_label || m}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {winners.map((w) => (
                      <div
                        key={w.id}
                        className={`p-3 rounded-2xl border ${rankTone[w.rank] || "bg-muted/30 border-border/60"}`}
                        data-testid={`hof-rank-${m}-${w.rank}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-70">#{w.rank} · {w.rank_label}</p>
                            <p className="font-display text-base font-black mt-0.5 truncate">{w.user_name}</p>
                            <p className="text-[11px] text-muted-foreground mt-0.5">{w.points} pts · {w.metrics?.deals_closed ?? 0} deals · {w.metrics?.meetings ?? 0} meetings</p>
                          </div>
                          <Crown className="h-4 w-4 shrink-0 opacity-80" />
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-2">
                          <Badge variant="outline" className="bg-white/60 border-current text-[10px] uppercase tracking-wider">
                            +{w.bonus_points} bonus
                          </Badge>
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/performance/monthly-winners/${w.id}/card.png`}
                            target="_blank"
                            rel="noreferrer"
                            download
                            className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-white/60 border border-current/30 hover:bg-white transition tap"
                            data-testid={`hof-download-${w.id}`}
                            title="Download shareable PNG card"
                          >
                            <Download className="h-3 w-3" /> Card
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Leaderboard tab ───────────────────────────────────────────────────────

function LeadGrowthPlanCard() {
  // Standalone leaderboard for the Lead Growth Plan discipline metric.
  // Surfaces who's actively prospecting via the LGP channel this month +
  // all-time so admins can spot under-performers at a glance.
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ month: null, team_month_total: 0, team_all_time_total: 0 });

  useEffect(() => {
    setLoading(true);
    api.get("/performance/lead-growth-plan/leaderboard")
      .then((r) => {
        setRows(r.data.rows || []);
        setMeta({
          month: r.data.month,
          team_month_total: r.data.team_month_total || 0,
          team_all_time_total: r.data.team_all_time_total || 0,
        });
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card className="rounded-3xl border-border/60 shadow-soft" data-testid="lead-growth-plan-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-primary">Discipline metric · {meta.month}</p>
            <h3 className="font-display text-2xl font-extrabold tracking-tight mt-1">Lead Growth Plan</h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-md leading-snug">
              Per-rep count of leads added via the structured Lead Growth Plan channel. North-star for outbound discipline.
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Team total</p>
            <p className="font-display text-3xl font-black tabular-nums leading-none mt-0.5">{meta.team_month_total}</p>
            <p className="text-[10px] text-muted-foreground tabular-nums mt-0.5">{meta.team_all_time_total} all-time</p>
          </div>
        </div>
        {loading ? (
          <div className="py-6 text-center text-xs text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-center text-xs text-muted-foreground">
            No reps have added Lead Growth Plan leads yet this month.
          </div>
        ) : (
          <div className="space-y-1.5">
            {rows.slice(0, 10).map((r, idx) => {
              const max = Math.max(1, rows[0].month_count);
              const pct = Math.min(100, (r.month_count / max) * 100);
              return (
                <Link
                  to={`/performance/users/${r.user_id}`}
                  key={r.user_id}
                  className="flex items-center gap-3 px-3 py-2 rounded-2xl hover:bg-muted/30 transition tap"
                  data-testid={`lgp-row-${r.user_id}`}
                >
                  <span className="w-6 text-[10px] uppercase tracking-wider font-bold text-muted-foreground tabular-nums text-center">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm font-semibold truncate">{r.user_name}</p>
                      <p className="text-xs tabular-nums text-muted-foreground">
                        <span className="font-bold text-foreground">{r.month_count}</span>
                        <span className="opacity-60"> / {r.all_time_count} total</span>
                      </p>
                    </div>
                    <div className="h-1.5 mt-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function LeaderboardTab({ isAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeAll, setIncludeAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/performance/leaderboard", { params: { include_all: includeAll } });
      setRows(data.rows || []);
    } catch (err) { toast.error(fmtError(err)); }
    finally { setLoading(false); }
  }, [includeAll]);

  useEffect(() => { load(); }, [load]);

  const qualified = rows.filter((r) => r.qualified);
  const disqualified = rows.filter((r) => !r.qualified);

  return (
    <div className="space-y-6" data-testid="tab-leaderboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">Monthly leaderboard</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Only qualified reps rank for prizes. Unlock by hitting all 5 targets.</p>
        </div>
        <div className="flex items-center gap-3">
          <Label className="text-xs font-semibold flex items-center gap-2" htmlFor="lb-all">
            <Switch id="lb-all" checked={includeAll} onCheckedChange={setIncludeAll} data-testid="lb-include-all" />
            Show disqualified too
          </Label>
        </div>
      </div>

      {/* Lead Growth Plan discipline card — surfaces per-user LGP lead counts
          alongside the main qualified-reps leaderboard. Admin-only on the
          backend; non-admins see the empty state. */}
      {isAdmin && <LeadGrowthPlanCard />}

      {loading ? (
        <div className="py-10 text-center text-muted-foreground text-sm"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
      ) : qualified.length === 0 && !includeAll ? (
        <Card className="rounded-3xl border-border/60">
          <CardContent className="p-10 text-center space-y-2">
            <Trophy className="h-8 w-8 text-amber-500 mx-auto" />
            <p className="font-semibold">No one has qualified yet this month.</p>
            <p className="text-sm text-muted-foreground">Reps need ≥1 deal, ≥10 meetings, ≥25 follow-ups, ≥300 points, ≥3 stage moves.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Podium */}
          {qualified.slice(0, 3).length > 0 && (
            <div className="grid grid-cols-3 gap-3" data-testid="lb-podium">
              {[qualified[1], qualified[0], qualified[2]].map((r, idx) => {
                if (!r) return <div key={idx} />;
                const rank = r.rank;
                const height = rank === 1 ? "h-40" : rank === 2 ? "h-32" : "h-28";
                const colors = rank === 1 ? "bg-amber-50 border-amber-300" : rank === 2 ? "bg-stone-100 border-stone-300" : "bg-orange-50 border-orange-200";
                return (
                  <div key={r.user_id} className={`rounded-3xl border ${colors} p-4 flex flex-col justify-end ${height}`} data-testid={`lb-rank-${rank}`}>
                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-70">Rank {rank}</p>
                    <p className="font-display text-lg font-black mt-1 truncate">{r.name}</p>
                    <p className="text-xs text-muted-foreground">{r.points_for_rank} pts · {r.metrics.deals_closed} deals</p>
                    {r.active_streak_label && (
                      <Badge variant="outline" className="mt-2 bg-primary-50 text-primary-700 border-primary-200 text-[10px] uppercase w-fit">
                        <Flame className="h-3 w-3 mr-1" /> {r.active_streak} · {r.active_streak_label}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Full list */}
          <Card className="rounded-3xl border-border/60">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    <th className="p-3 font-bold">#</th>
                    <th className="p-3 font-bold">Name</th>
                    <th className="p-3 font-bold text-right">Points</th>
                    <th className="p-3 font-bold text-right">Deals</th>
                    <th className="p-3 font-bold text-right">Meetings</th>
                    <th className="p-3 font-bold text-right">Follow-ups</th>
                    <th className="p-3 font-bold">Streak</th>
                    <th className="p-3 font-bold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.user_id} className="border-t border-border/40 hover:bg-muted/20 cursor-pointer" data-testid={`lb-row-${r.user_id}`}>
                      <td className="p-3 font-mono text-xs font-bold">
                        <Link to={`/performance/users/${r.user_id}`} className="block">{r.rank ?? "—"}</Link>
                      </td>
                      <td className="p-3 font-semibold">
                        <Link to={`/performance/users/${r.user_id}`} className="block hover:text-primary">
                          {r.name}
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mt-0.5">{r.role}</div>
                        </Link>
                      </td>
                      <td className="p-3 text-right tabular-nums font-bold">{r.points_for_rank}</td>
                      <td className="p-3 text-right tabular-nums">{r.metrics.deals_closed}</td>
                      <td className="p-3 text-right tabular-nums">{r.metrics.meetings}</td>
                      <td className="p-3 text-right tabular-nums">{r.metrics.followups}</td>
                      <td className="p-3 text-right tabular-nums font-semibold text-primary">{r.metrics.leads_growth_plan ?? 0}</td>
                      <td className="p-3 text-xs">{r.active_streak ? `${r.active_streak}d${r.active_streak_label ? ` · ${r.active_streak_label}` : ""}` : "—"}</td>
                      <td className="p-3">{r.qualified ? (
                        <Badge variant="outline" className="bg-primary-50 text-primary-700 border-primary-200 text-[10px] uppercase"><CheckCircle2 className="h-3 w-3 mr-1" />Qualified</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-stone-100 text-stone-600 border-stone-200 text-[10px] uppercase"><XCircle className="h-3 w-3 mr-1" />Not yet</Badge>
                      )}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {includeAll && disqualified.length > 0 && (
            <p className="text-xs text-muted-foreground text-center">{disqualified.length} rep(s) below qualification threshold</p>
          )}
        </>
      )}

      <HallOfFame isAdmin={isAdmin} />
    </div>
  );
}

// ── Team Overview tab ─────────────────────────────────────────────────────

function TeamOverviewTab() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/performance/admin/overview");
      setOverview(data);
    } catch (err) { toast.error(fmtError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;
  if (!overview) return null;

  const CHECK_LABELS = {
    deals_closed: "Deals", meetings: "Meetings", followups: "Follow-ups", points: "Points", stage_moves: "Stage moves",
  };

  return (
    <div className="space-y-6" data-testid="tab-team">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBox label="Team members" value={overview.totals.users} data-testid="team-count-users" />
        <StatBox label="Qualified" value={overview.totals.qualified} tone="ok" data-testid="team-count-qualified" />
        <StatBox label="Disqualified" value={overview.totals.disqualified} tone="warn" data-testid="team-count-disqualified" />
        <StatBox label="Streaks at risk" value={overview.totals.at_risk} tone="bad" data-testid="team-count-at-risk" />
      </div>

      <Card className="rounded-3xl border-border/60">
        <CardContent className="p-4 space-y-3">
          {overview.rows.map((r) => (
            <Link
              key={r.user_id}
              to={`/performance/users/${r.user_id}`}
              className="block p-4 rounded-2xl border border-border/60 bg-muted/10 hover:bg-muted/20 hover:border-primary/40 tap transition"
              data-testid={`team-row-${r.user_id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold">{r.name}</p>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mt-0.5">{r.role}</p>
                </div>
                <div className="flex items-center gap-2">
                  {r.qualified ? (
                    <Badge variant="outline" className="bg-primary-50 text-primary-700 border-primary-200 text-[10px] uppercase"><CheckCircle2 className="h-3 w-3 mr-1" />Qualified</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-stone-100 text-stone-600 border-stone-200 text-[10px] uppercase">{r.next_gaps.length} gap(s)</Badge>
                  )}
                  <Badge variant="outline" className="bg-card border-border/60 text-[10px] uppercase">{r.metrics.total_points} pts</Badge>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-3">
                {Object.entries(r.checks).map(([k, v]) => (
                  <div key={k} className="space-y-1">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">{CHECK_LABELS[k]}</span>
                      <span className={`font-semibold tabular-nums ${v.passed ? "text-primary-700" : "text-muted-foreground"}`}>{v.value}/{v.target}</span>
                    </div>
                    <ProgressBar value={v.value} target={v.target} />
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-border/40 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {r.streaks.map((s) => (
                  <span key={s.type} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/40">
                    <Flame className="h-3 w-3" />
                    {s.type === "daily_followup" ? "Daily" : s.type === "weekly_activity" ? "Weekly" : "Monthly"}: {s.count}{s.level ? ` · ${s.level}` : ""}
                    {s.at_risk && <AlertTriangle className="h-3 w-3 text-red-500 ml-1" />}
                  </span>
                ))}
                <span className="ml-auto">{r.milestones_count} milestone(s)</span>
              </div>
            </Link>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Milestones tab ────────────────────────────────────────────────────────

function MilestonesTab() {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/performance/admin/milestones-log", { params: { limit: 100 } })
      .then((r) => setFeed(r.data.rows || []))
      .catch((err) => toast.error(fmtError(err)))
      .finally(() => setLoading(false));
  }, []);

  const TIER_TONE = { 1: "stone", 2: "sky", 3: "amber", 4: "primary", 5: "violet" };
  const tone = (t) => ({
    stone: "bg-stone-100 text-stone-700 border-stone-200",
    sky: "bg-sky-50 text-sky-800 border-sky-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    primary: "bg-primary-50 text-primary-700 border-primary-200",
    violet: "bg-violet-50 text-violet-800 border-violet-200",
  }[t] || "bg-muted");

  return (
    <div className="space-y-5" data-testid="tab-milestones">
      <div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight">Milestone activity log</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Every unlock awards a one-time bonus point block. Pop-up notifications follow the settings audience.</p>
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
      ) : feed.length === 0 ? (
        <Card className="rounded-3xl border-border/60">
          <CardContent className="p-10 text-center space-y-2">
            <Medal className="h-8 w-8 text-amber-500 mx-auto" />
            <p className="font-semibold">No milestones unlocked yet.</p>
            <p className="text-sm text-muted-foreground">The first deal closed, 50 meetings, 500 follow-ups — unlocks will show up here.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-3xl border-border/60">
          <CardContent className="p-4 space-y-2">
            {feed.map((m) => (
              <div key={m.id} className="flex items-start gap-3 p-3 rounded-2xl bg-muted/20 border border-border/50" data-testid={`milestone-${m.id}`}>
                <div className={`h-10 w-10 grid place-items-center rounded-2xl border ${tone(TIER_TONE[m.tier])} shrink-0`}>
                  <Medal className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm">{m.user_name}</p>
                    <Badge variant="outline" className={`text-[10px] uppercase ${tone(TIER_TONE[m.tier])}`}>{m.tier_label}</Badge>
                  </div>
                  <p className="text-sm mt-0.5">{m.label}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">+{m.bonus_points} bonus · {new Date(m.achieved_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Streaks tab ───────────────────────────────────────────────────────────

function StreaksTab() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/performance/admin/overview")
      .then((r) => setOverview(r.data))
      .catch((err) => toast.error(fmtError(err)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;
  if (!overview) return null;

  const atRisk = overview.rows.filter((r) => r.streaks.some((s) => s.at_risk));
  const active = overview.rows.filter((r) => r.streaks.some((s) => (s.count || 0) > 0));

  const renderStreakCell = (s) => {
    const lbl = s.type === "daily_followup" ? "Daily" : s.type === "weekly_activity" ? "Weekly" : "Monthly";
    return (
      <span key={s.type} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs ${s.at_risk ? "bg-red-50 text-red-700 border-red-200" : s.level ? "bg-primary-50 text-primary-700 border-primary-200" : "bg-muted/40 text-muted-foreground border-border/60"}`}>
        <Flame className="h-3 w-3" />
        <span className="font-semibold">{lbl}:</span> {s.count}{s.level ? ` · ${s.level}` : ""}
        {s.at_risk && <AlertTriangle className="h-3 w-3 ml-0.5" />}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="tab-streaks">
      <div className="grid grid-cols-2 gap-3">
        <StatBox label="Streaks at risk" value={atRisk.length} tone="bad" data-testid="streaks-at-risk" />
        <StatBox label="With active streaks" value={active.length} tone="ok" data-testid="streaks-active" />
      </div>

      {atRisk.length > 0 && (
        <div>
          <h3 className="font-display text-lg font-extrabold tracking-tight mb-2 flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-4 w-4" /> At-risk today
          </h3>
          <Card className="rounded-3xl border-red-200">
            <CardContent className="p-4 space-y-2">
              {atRisk.map((r) => (
                <div key={r.user_id} className="p-3 rounded-2xl bg-red-50/50 border border-red-200/60" data-testid={`at-risk-${r.user_id}`}>
                  <p className="font-semibold">{r.name}</p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {r.streaks.map(renderStreakCell)}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      <div>
        <h3 className="font-display text-lg font-extrabold tracking-tight mb-2">Full roster</h3>
        <Card className="rounded-3xl border-border/60">
          <CardContent className="p-4 space-y-2">
            {overview.rows.map((r) => (
              <div key={r.user_id} className="flex flex-wrap items-center gap-3 p-3 rounded-2xl bg-muted/10 border border-border/40">
                <span className="font-semibold text-sm min-w-[140px]">{r.name}</span>
                <div className="flex flex-wrap gap-1.5">{r.streaks.map(renderStreakCell)}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Flags tab (anti-gaming review) ───────────────────────────────────────

const FLAG_SEVERITY_TONE = {
  low: "bg-sky-50 text-sky-800 border-sky-200",
  med: "bg-amber-50 text-amber-800 border-amber-200",
  high: "bg-red-50 text-red-800 border-red-200",
};

const FLAG_STATUS_TONE = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  dismissed: "bg-stone-100 text-stone-700 border-stone-200",
  invalidated: "bg-red-50 text-red-800 border-red-200",
};

function FlagsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/performance/flags", { params: { status: statusFilter || undefined } });
      setRows(data.rows || []);
    } catch (err) { toast.error(fmtError(err)); }
    finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const review = async (id, action) => {
    const label = action === "dismiss" ? "Dismiss this flag (false positive)?" : "Invalidate the linked points? This removes them from the user's totals and leaderboard.";
    if (!window.confirm(label)) return;
    try {
      await api.post(`/performance/flags/${id}/review`, { action });
      toast.success(action === "dismiss" ? "Flag dismissed" : "Points invalidated");
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/performance/admin/scan-anti-gaming");
      toast.success(`Scan complete — ${data.flags_created} new flag(s)`);
      load();
    } catch (err) { toast.error(fmtError(err)); }
    finally { setScanning(false); }
  };

  const runInactivity = async () => {
    if (!window.confirm("Apply the 7-day inactivity penalty (-5 pts) to every rep with no recent activity? Idempotent per ISO-week.")) return;
    try {
      const { data } = await api.post("/performance/admin/run-inactivity-penalty");
      toast.success(`${data.penalties_applied} penalty point event(s) applied`);
    } catch (err) { toast.error(fmtError(err)); }
  };

  const runStreakNotif = async () => {
    try {
      const { data } = await api.post("/performance/admin/run-perf-notifications");
      toast.success(`Sent — ${data.morning_sent} morning · ${data.streak_risk_sent} streak-risk`);
    } catch (err) { toast.error(fmtError(err)); }
  };

  const sendTestStreakNotif = async () => {
    try {
      await api.post("/performance/admin/test-streak-notification");
      toast.success("Test streak notification fired — check toast / Home banner");
    } catch (err) { toast.error(fmtError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="tab-flags">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">Flags & integrity</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Auto-detected gaming patterns — burst completions, stage bouncing. Review and invalidate or dismiss.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <Button variant="outline" size="sm" onClick={runScan} disabled={scanning} className="rounded-2xl tap text-xs" data-testid="run-anti-gaming-scan">
            {scanning ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Shield className="h-3.5 w-3.5 mr-1" />}
            Scan now
          </Button>
          <Button variant="outline" size="sm" onClick={runInactivity} className="rounded-2xl tap text-xs" data-testid="run-inactivity-penalty">
            <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Run 7-day inactivity penalty
          </Button>
          <Button variant="outline" size="sm" onClick={runStreakNotif} className="rounded-2xl tap text-xs" data-testid="run-perf-notifications">
            <Flame className="h-3.5 w-3.5 mr-1" /> Run streak notifications
          </Button>
          <Button variant="outline" size="sm" onClick={sendTestStreakNotif} className="rounded-2xl tap text-xs" data-testid="send-test-streak-notif">
            <Flame className="h-3.5 w-3.5 mr-1" /> Send test streak notif
          </Button>
        </div>
      </div>

      <div className="flex gap-1.5 flex-wrap" data-testid="flag-status-filter">
        {[
          { k: "pending", label: "Pending" },
          { k: "invalidated", label: "Invalidated" },
          { k: "dismissed", label: "Dismissed" },
          { k: "", label: "All" },
        ].map((f) => (
          <button
            key={f.k || "all"}
            onClick={() => setStatusFilter(f.k)}
            className={`px-3 h-8 rounded-full text-xs font-semibold tap transition ${statusFilter === f.k ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground"}`}
            data-testid={`flag-filter-${f.k || "all"}`}
          >{f.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <Card className="rounded-3xl border-border/60">
          <CardContent className="p-10 text-center space-y-2">
            <Shield className="h-8 w-8 text-primary mx-auto" />
            <p className="font-semibold">Nothing flagged.</p>
            <p className="text-sm text-muted-foreground">Team activity looks clean. Scans run daily at 04:30 UTC.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-3xl border-border/60">
          <CardContent className="p-4 space-y-2">
            {rows.map((f) => (
              <div key={f.id} className="p-4 rounded-2xl border border-border/60 bg-muted/10" data-testid={`flag-${f.id}`}>
                {/* Stack on mobile so user-name + severity badges aren't
                    crammed against the action buttons. Actions move to a
                    separate row below the details on small screens. */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold break-words">{f.user_name}</p>
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${FLAG_SEVERITY_TONE[f.severity] || "bg-muted"}`}>{f.severity}</Badge>
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${FLAG_STATUS_TONE[f.status] || "bg-muted"}`}>{f.status}</Badge>
                    </div>
                    <p className="text-sm font-semibold mt-1 break-words">{f.flag_type.replace(/_/g, " ")}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 break-words">{f.details}</p>
                    <p className="text-[10px] text-muted-foreground mt-1 break-words">
                      {f.source_ids?.length || 0} event(s) linked · created {new Date(f.created_at).toLocaleString()}
                      {f.reviewed_at ? ` · reviewed ${new Date(f.reviewed_at).toLocaleString()}` : ""}
                    </p>
                    {f.review_note && <p className="text-[11px] text-muted-foreground mt-0.5 italic break-words">Note: {f.review_note}</p>}
                  </div>
                  {f.status === "pending" && (
                    <div className="flex gap-1.5 sm:flex-col sm:gap-1.5 sm:shrink-0 flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => review(f.id, "dismiss")} className="rounded-2xl tap text-xs sm:w-full justify-start" data-testid={`flag-dismiss-${f.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Dismiss
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => review(f.id, "invalidate")} className="rounded-2xl tap text-xs text-red-700 hover:bg-red-50 border-red-200 sm:w-full justify-start" data-testid={`flag-invalidate-${f.id}`}>
                        <Ban className="h-3.5 w-3.5 mr-1" /> Invalidate points
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Settings tab ──────────────────────────────────────────────────────────

function SettingsTab({ onChanged }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/performance/settings");
      setSettings(data);
    } catch (err) { toast.error(fmtError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/performance/settings", patch);
      setSettings(data);
      toast.success("Settings updated");
      onChanged?.(data);
    } catch (err) { toast.error(fmtError(err)); }
    finally { setSaving(false); }
  };

  if (loading || !settings) return <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;

  const Q = settings.qualification || {};

  return (
    <div className="space-y-6" data-testid="tab-settings">
      {/* Release mode */}
      <Card className="rounded-3xl border-border/60">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-display text-lg font-extrabold tracking-tight flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" /> Release Performance System to Users
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">Control who sees the Performance Hub in their navigation.</p>
            </div>
            <ReleaseBadge mode={settings.release_mode} />
          </div>
          <Select value={settings.release_mode} onValueChange={(v) => save({ release_mode: v })}>
            <SelectTrigger className="rounded-2xl h-11 max-w-sm" data-testid="release-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft — admin only (current)</SelectItem>
              <SelectItem value="internal">Internal test — admin + selected users</SelectItem>
              <SelectItem value="live">Live — all eligible users</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Monthly qualification */}
      <Card className="rounded-3xl border-border/60">
        <CardContent className="p-5 space-y-4">
          <div>
            <h3 className="font-display text-lg font-extrabold tracking-tight">Monthly qualification targets</h3>
            <p className="text-sm text-muted-foreground mt-0.5">Reps must hit every target to appear on the prize leaderboard.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { k: "deals_closed_min", label: "Deals" },
              { k: "meetings_min", label: "Meetings" },
              { k: "followups_min", label: "Follow-ups" },
              { k: "points_min", label: "Points" },
              { k: "stage_moves_min", label: "Stage moves" },
            ].map((f) => (
              <div key={f.k}>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{f.label}</Label>
                <Input
                  type="number"
                  className="rounded-2xl h-11 mt-1"
                  defaultValue={Q[f.k]}
                  onBlur={(e) => {
                    const v = Number(e.target.value) || 0;
                    if (v !== Q[f.k]) save({ qualification: { ...Q, [f.k]: v } });
                  }}
                  data-testid={`q-${f.k}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Daily streak rules */}
      <Card className="rounded-3xl border-border/60">
        <CardContent className="p-5 space-y-4">
          <div>
            <h3 className="font-display text-lg font-extrabold tracking-tight">Daily streak requirements</h3>
            <p className="text-sm text-muted-foreground mt-0.5">Both targets must be hit each day for the day to count toward the streak. Voice journals are the rep's daily client-update log.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            {[
              { k: "voice_required", label: "Voice journals / day", help: "Transcribed voice updates" },
              { k: "followups_required", label: "Follow-ups / day", help: "Completed follow-ups" },
            ].map((f) => (
              <div key={f.k}>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{f.label}</Label>
                <Input
                  type="number"
                  min="0"
                  className="rounded-2xl h-11 mt-1"
                  defaultValue={(settings.streak_rules?.daily_followup?.[f.k]) ?? (f.k === "followups_required" ? settings.streak_rules?.daily_followup?.daily_required : 1) ?? 1}
                  onBlur={(e) => {
                    const v = Math.max(0, Number(e.target.value) || 0);
                    const cur = settings.streak_rules?.daily_followup || {};
                    if (v !== cur[f.k]) {
                      const nextDaily = { ...cur, [f.k]: v };
                      // Drop the legacy `daily_required` once the user touches followups so it can't drift.
                      if (f.k === "followups_required") delete nextDaily.daily_required;
                      save({ streak_rules: { ...(settings.streak_rules || {}), daily_followup: nextDaily } });
                    }
                  }}
                  data-testid={`streak-${f.k}`}
                />
                <p className="text-[10px] text-muted-foreground mt-1">{f.help}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Milestone / leaderboard controls */}
      <Card className="rounded-3xl border-border/60">
        <CardContent className="p-5 space-y-4">
          <h3 className="font-display text-lg font-extrabold tracking-tight">Milestone & leaderboard</h3>
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="font-semibold">Include milestone bonus points in leaderboard ranking</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Off = leaderboard reflects only monthly activity points.</p>
            </div>
            <Switch
              checked={!!settings.include_bonus_in_leaderboard}
              onCheckedChange={(v) => save({ include_bonus_in_leaderboard: v })}
              data-testid="include-bonus-switch"
            />
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="font-semibold">Enable milestone pop-up notifications</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Shown as a global toast when any user unlocks a milestone.</p>
            </div>
            <Switch
              checked={!!settings.milestone_popup_enabled}
              onCheckedChange={(v) => save({ milestone_popup_enabled: v })}
              data-testid="popup-enabled-switch"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Pop-up audience</Label>
            <Select value={settings.milestone_popup_audience} onValueChange={(v) => save({ milestone_popup_audience: v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1 max-w-sm" data-testid="popup-audience-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone in the app</SelectItem>
                <SelectItem value="admin_manager">Admin + managers only</SelectItem>
                <SelectItem value="admin">Admin only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {saving && <p className="text-xs text-muted-foreground text-center">Saving…</p>}
    </div>
  );
}

// ── Main Performance Hub ──────────────────────────────────────────────────

export default function Performance() {
  const { user } = useAuth();
  const [tab, setTab] = useState("leaderboard");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/performance/me")
      .then((r) => setSummary(r.data))
      .catch(() => {});
  }, []);

  if (user?.role !== "admin") {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Card className="rounded-3xl border-border/60">
          <CardContent className="p-10 text-center space-y-3">
            <Sparkles className="h-10 w-10 text-primary mx-auto" />
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Performance Hub</h1>
            <p className="text-sm text-muted-foreground">Being tuned up. Your admin will release it to the team once the point values and targets are finalized.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-[1280px] mx-auto" data-testid="performance-hub">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground flex items-center gap-2">
            <TrendingUp className="h-3 w-3" /> Admin performance console
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Performance Hub.</h1>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">Points, streaks, milestones, qualification. Admin-only in Draft mode — flip release mode once you're ready.</p>
        </div>
        {summary && summary.release_mode && <ReleaseBadge mode={summary.release_mode} />}
      </header>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto mb-6 pb-1 -mx-1 px-1 scrollbar-none" data-testid="perf-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-4 h-10 rounded-2xl text-sm font-semibold tap transition ${active ? "bg-primary text-primary-foreground shadow-soft" : "bg-muted/40 text-muted-foreground hover:bg-muted"}`}
              data-testid={`perf-tab-${t.key}`}
            >
              <Icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "leaderboard" && <LeaderboardTab isAdmin={user?.role === "admin"} />}
      {tab === "team" && <TeamOverviewTab />}
      {tab === "milestones" && <MilestonesTab />}
      {tab === "streaks" && <StreaksTab />}
      {tab === "flags" && <FlagsTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}
